import { Header } from '@/components/Header';
import { Button, Card } from '@analytical-alley/ui';
import type { ErrorComponentProps } from '@tanstack/react-router';

/**
 * Route-level error screen for the dashboard routes.
 *
 * Two things distinguish it from the generic {@link ErrorScreen} used as the
 * app-wide `defaultErrorComponent`:
 *
 * 1. **It renders the navigation.** Each dashboard renders `Header` itself,
 *    inside the provider subtree that throws, so the router boundary unmounts
 *    the nav along with the failed dashboard and strands the user (AAPD-678).
 *    Rendering `Header` here restores the way out. It resolves because
 *    `DashboardGlobalProvider` and the ability context live in the parent
 *    `_dashboards` layout and stay mounted.
 * 2. **The technical message is collapsed, not dumped.** The previous screen
 *    printed a full DuckDB `SELECT` as body text, which reads as gibberish to a
 *    customer. It is still present and selectable behind a disclosure, because a
 *    legible error is what lets someone report a crash accurately instead of
 *    reporting "the dashboard is broken".
 */
export const DashboardErrorScreen = ({ error, reset }: ErrorComponentProps) => {
    const technicalMessage =
        error instanceof Error ? error.message : String(error);

    return (
        <>
            <Header
                className="pb-6 px-8 lg:px-16 pt-6"
                title="Dashboard unavailable"
            />
            <div className="pb-8 px-8 lg:px-16">
                <Card>
                    <div className="flex flex-col gap-4 py-8 px-4 mx-auto max-w-(--breakpoint-xl) lg:py-12 lg:px-6">
                        <p className="text-3xl tracking-tight font-bold text-gray-900 md:text-4xl dark:text-white">
                            This dashboard could not be displayed
                        </p>
                        <p className="text-gray-500 dark:text-gray-400">
                            Something in this dashboard&apos;s data could not be
                            read. The rest of the platform is unaffected — use
                            the menu to open another dashboard. If this keeps
                            happening, send the technical details below to
                            support.
                        </p>
                        <details className="text-sm">
                            <summary className="cursor-pointer text-gray-500 dark:text-gray-400">
                                Technical details
                            </summary>
                            <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md bg-black/20 p-3 font-mono text-xs text-gray-700 dark:text-gray-300">
                                {technicalMessage}
                            </pre>
                        </details>
                        <div>
                            <Button size="sm" color="blue" onClick={reset}>
                                Try again
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        </>
    );
};
